import { Dossier, DossierStatusGroupMap } from "../types/Dossier";
import { Quote } from "../types/Quote";
import { isBefore } from "./dates";

const isOpenDossier = (dossier: Dossier) => {
  return DossierStatusGroupMap["open"].includes(dossier.status);
};

export function hasOpenDossier(dossiers: Dossier[]) {
  return dossiers.some((dossier) => isOpenDossier(dossier));
}

export const getLatestDossier = (dossiers: Dossier[]) => {
  return dossiers.reduce((prev: Dossier | null, current) => {
    if (!prev || isBefore(prev.startDate, current.startDate)) {
      return current;
    }
    return prev;
  }, null);
};

export const getLatestOpenDossier = (dossiers: Dossier[]) => {
  return dossiers.reduce((prev: Dossier | null, current) => {
    if (
      isOpenDossier(current) &&
      (!prev || isBefore(prev.startDate, current.startDate))
    ) {
      return current;
    }
    return prev;
  }, null);
};

export const getLatestActiveDossier = (dossiers: Dossier[]) => {
  return dossiers.reduce((prev: Dossier | null, current) => {
    if (
      isDossierActive(current) &&
      (!prev || isBefore(prev.startDate, current.startDate))
    ) {
      return current;
    }
    return prev;
  }, null);
};

export const getLatestActivatedDossier = (dossiers: Dossier[]) => {
  return dossiers.reduce((prev: Dossier | null, current) => {
    if (
      isDossierActivated(current) &&
      (!prev || isBefore(prev.startDate, current.startDate))
    ) {
      return current;
    }
    return prev;
  }, null);
};

export const isDossierVisible = (dossier: Dossier) => {
  return ["New", "Active", "Closed", "Closing"].includes(dossier.status);
};

export const isDossierActivated = (dossier: Dossier) => {
  return DossierStatusGroupMap["activated"].includes(dossier.status);
};

export const isDossierActive = (dossier: Dossier) => {
  return DossierStatusGroupMap["active"].includes(dossier.status);
};

export const isDossierCancelled = (dossier: Dossier) => {
  return DossierStatusGroupMap["cancelled"].includes(dossier.status);
};

export const getDossierStatusGroup = (dossier: Dossier) => {
  if (isDossierActive(dossier)) {
    return "active";
  } else if (isDossierActivated(dossier)) {
    return "activated";
  } else if (isOpenDossier(dossier)) {
    return "open";
  } else {
    return "cancelled";
  }
};

const dossierListStatusOrder = {
  New: 1,
  Active: 2,
  Closed: 4,
  Closing: 3,
  Cancelled: 5,
};

const activatedDossierStatusOrder = {
  Active: 1,
  Closing: 2,
  Closed: 3,
  New: 4,
  Cancelled: 5,
};

const sortDossiersByStatus = (
  dossiers: Dossier[],
  statusOrder: Record<string, number>
) => {
  const dossiersToSort = [...dossiers];
  dossiersToSort.sort((a, b) => {
    // First sort by status
    const aStatusOrder = statusOrder[a.status] || 999;
    const bStatusOrder = statusOrder[b.status] || 999;

    if (aStatusOrder !== bStatusOrder) {
      return aStatusOrder - bStatusOrder;
    }

    // Then sort by start date descending (newest first)
    const aDate = new Date(a.startDate);
    const bDate = new Date(b.startDate);
    return bDate.getTime() - aDate.getTime();
  });

  return dossiersToSort;
};

const sortDossiersByCreatedDate = (dossiers: Dossier[]) => {
  return dossiers.sort((a, b) => {
    return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
  });
};

export const filterVisibleDossiers = (dossiers: Dossier[]) => {
  const filteredDossiers = dossiers.filter((dossier) =>
    isDossierVisible(dossier)
  );
  return sortDossiersByStatus(filteredDossiers, dossierListStatusOrder);
};

export const filterOpenDossiers = (dossiers: Dossier[]) => {
  const filteredDossiers = dossiers.filter((dossier) => isOpenDossier(dossier));
  return sortDossiersByCreatedDate(filteredDossiers);
};

export const filterActivatedDossiers = (dossiers: Dossier[]) => {
  const filteredDossiers = dossiers.filter((dossier) =>
    isDossierActivated(dossier)
  );
  return sortDossiersByStatus(filteredDossiers, activatedDossierStatusOrder);
};

export const filterActiveDossiers = (dossiers: Dossier[]) => {
  const filteredDossiers = dossiers.filter((dossier) =>
    isDossierActive(dossier)
  );
  return sortDossiersByStatus(filteredDossiers, activatedDossierStatusOrder);
};

export const filterUncancelledDossiers = (dossiers: Dossier[]) => {
  const filteredDossiers = dossiers.filter(
    (dossier) => !isDossierCancelled(dossier)
  );
  return sortDossiersByStatus(filteredDossiers, activatedDossierStatusOrder);
};

export const isActivatedDossierPage = () => {
  const activatedCheck = /^\/dossiers\/[^/]+\/activated$/;
  const expenseClaimsCheck = /^\/dossiers\/[^/]+\/expense-claims$/;
  const createExpenseClaimCheck = /^\/dossiers\/[^/]+\/expense-claim\/create$/;
  const transactionsCheck = /^\/dossiers\/[^/]+\/transactions$/;
  const budgetsCheck = /^\/dossiers\/[^/]+\/budgets$/;
  const budgetCheck = /^\/dossiers\/[^/]+\/budgets\/[^/]+$/;
  const balancesCheck = /^\/dossiers\/[^/]+\/balances$/;
  const documentsCheck = /^\/dossiers\/[^/]+\/documents$/;
  const terminateCheck = /^\/dossiers\/[^/]+\/terminate$/;
  const terminationCheck = /^\/dossiers\/[^/]+\/termination$/;
  const pathname = window.location.pathname;

  return (
    activatedCheck.test(pathname) ||
    expenseClaimsCheck.test(pathname) ||
    createExpenseClaimCheck.test(pathname) ||
    transactionsCheck.test(pathname) ||
    budgetsCheck.test(pathname) ||
    budgetCheck.test(pathname) ||
    balancesCheck.test(pathname) ||
    documentsCheck.test(pathname) ||
    terminateCheck.test(pathname) ||
    terminationCheck.test(pathname)
  );
};

export const isEmployerPage = () => {
  const employerCheck = /^\/employer-[^/]+(\/.+)?$/;
  const pathname = window.location.pathname;

  return employerCheck.test(pathname);
};

export const canRequestDealerPricing = (dossier: Dossier, quote: Quote) => {
  const dossierType = dossier.type;
  const opportunity = dossier.opportunity;
  const isPrimary =
    opportunity?.primaryQuoteId === quote.identifiers.crmQuoteId;

  return (
    isOpenDossier(dossier) &&
    !!opportunity &&
    !opportunity.driverSecuredVehicle &&
    !opportunity.taxInvoiceRequested &&
    (dossierType === "new" || dossierType === "used") &&
    isPrimary
  );
};

export const canAccessVehicleSourcingFlow = (dossier: Dossier): boolean => {
  const opportunity = dossier.opportunity;
  if (!opportunity) {
    return false;
  }

  return (
    !opportunity.driverVehicleOrderUploaded &&
    !opportunity.driverRequestedVehiclePricing &&
    !opportunity.vehiclePricingRequested &&
    opportunity.driverExecutedPackagingAgreement
  );
};

export const isAiConsentProtectedRoute = () => {
  const suggestionsCheck = /^\/quote\/[^/]+\/vehicle-suggestions$/;
  const reviewCheck = /^\/dossiers\/[^/]+\/vehicle-review\/[^/]+$/;
  const pathname = window.location.pathname;

  return suggestionsCheck.test(pathname) || reviewCheck.test(pathname);
};

const getCRMUrl = (crmHost: string, entity: string, id: string) => {
  return `${crmHost}&pagetype=entityrecord&etn=${entity}&id=${id}`;
};

export const getAccountCRMUrl = (crmHost: string, accountId: string) => {
  return getCRMUrl(crmHost, "account", accountId);
};

export const getContactCRMUrl = (crmHost: string, contactId: string) => {
  return getCRMUrl(crmHost, "contact", contactId);
};

export const getInvoiceCRMUrl = (crmHost: string, invoiceId: string) => {
  return getCRMUrl(crmHost, "new_invoice_autopilot", invoiceId);
};

export const getDossierCRMUrl = (crmHost: string, dossierId: string) => {
  return getCRMUrl(crmHost, "new_dossier", dossierId);
};

export const getOpportunityCRMUrl = (
  crmHost: string,
  opportunityId: string
) => {
  return getCRMUrl(crmHost, "opportunity", opportunityId);
};

export const getDossierTransactionCRMUrl = (
  crmHost: string,
  transactionId: string
) => {
  return getCRMUrl(crmHost, "new_dossiertransaction", transactionId);
};

export const getVehicleDealerCRMUrl = (crmHost: string, dealerId: string) => {
  return getCRMUrl(crmHost, "new_vehicledealer", dealerId);
};

export const getVehiclePricingRequestCRMUrl = (
  crmHost: string,
  pricingRequestId: string
) => {
  return getCRMUrl(crmHost, "new_vehiclepricingrequest", pricingRequestId);
};
